import type { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import AutoOpenNoticeHost from 'src/components/AlertCenter/AutoOpenNoticeHost.component';
import { useUserContext } from '../UserProvider/User.context.ts';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context.ts';
import { AlertCenterContext, DEFAULT_ALERT_CENTER_CONTEXT } from './AlertCenter.context';
import AlertCenterProvider from './AlertCenter.provider';

export type tAlertCenterBoundaryProps = {
  children: ReactNode;
};

/**
 * Tier 1 of the two-tier containment (RET-1871, pattern: `Chat.component.tsx`).
 *
 * `AlertCenterProvider` sits ABOVE every alert-consuming surface (Navbar's
 * `AlertBanner`/`AlertBell`), so a throw during snapshot assembly inside
 * `useAlertCenterValue` happens outside of what a consumer-only boundary
 * (Tier 2) can ever catch — React boundaries only catch throws from their own
 * subtree. This boundary wraps `AlertCenterProvider` directly instead.
 *
 * On catch, the fallback re-provides `DEFAULT_ALERT_CENTER_CONTEXT` (zero
 * alerts, every seam a no-op) around the same `children` the provider would
 * have wrapped, so every private page below keeps rendering with an empty
 * alert context instead of a blank screen.
 *
 * Accepted tradeoff: React remounts the whole subtree under an error boundary
 * once it catches — so this boundary catching a throw REMOUNTS the entire
 * private tree below it (every route, every page-level hook). That is a
 * worse outcome than a crash-free continuation but a better one than the
 * alternative, which is the private app shell crashing outright.
 */
export default function AlertCenterBoundary(props: tAlertCenterBoundaryProps) {
  const { currentWorkspace, currentWorkspaceId } = useWorkspaceContext();
  const { currentUserId } = useUserContext();
  /*
   * AGI-428: the automatic notice modal mounts HERE, inside the provider and
   * beside the private tree, so it is alive on whatever page the owner lands
   * on (the declaration carries no `surfaces`).
   *
   * Gated on both ids and keyed by the pair: the host reads storage scoped by
   * (workspace, user), and a mount with either id still undefined would read
   * an empty bucket and reopen a notice the user already switched off. See the
   * mount contract on the host, and
   * docs/solutions/logic-errors/late-entity-id-lazy-init-clobbers-persisted-state.md
   *
   * Deliberately NOT in the error-boundary fallback: that path re-provides the
   * empty context, where no alert is ever live, so a host there would have
   * nothing to open and one more thing to go wrong during a crash.
   */
  const autoNoticeScope =
    currentWorkspaceId && currentUserId
      ? { workspaceId: currentWorkspaceId, userId: currentUserId }
      : null;

  return (
    <Sentry.ErrorBoundary
      fallback={() => (
        <AlertCenterContext.Provider value={DEFAULT_ALERT_CENTER_CONTEXT}>
          {props.children}
        </AlertCenterContext.Provider>
      )}
      beforeCapture={(scope) => {
        scope.setTag('error_boundary', 'alert_center_provider');
        scope.setTag('workspace_id', currentWorkspace?.id ?? 'unknown');
        scope.setLevel('error');
      }}
    >
      <AlertCenterProvider>
        {props.children}
        {autoNoticeScope ? (
          <AutoOpenNoticeHost
            key={`${autoNoticeScope.workspaceId}:${autoNoticeScope.userId}`}
            workspaceId={autoNoticeScope.workspaceId}
            userId={autoNoticeScope.userId}
          />
        ) : null}
      </AlertCenterProvider>
    </Sentry.ErrorBoundary>
  );
}
